import React from "react";

import {
  FaCogs,
  FaStethoscope,
  FaUserMd,
  FaHospital,
  FaAmbulance,
  FaClinicMedical,
  FaCheck,
  FaArrowRight,
} from "react-icons/fa";
import { MdLocalHospital } from "react-icons/md";

import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ChallengesImage from "../assets/health-challenge.jpg";
import heroBg from "../assets/Health.jpg";

const HealthcareIndustry = () => {
  const services = [
    {
      icon: FaHospital,
      title: "Hospital Marketing",
      description:
        "Comprehensive digital strategies for hospitals and multi-specialty centers to attract patients and referrals.",
      features: [
        "Patient Acquisition",
        "Service Line Promotion",
        "Reputation Management",
        "Doctor & Department Spotlighting",
      ],
      color: "from-green-500 to-emerald-500",
      iconColor: "text-white",
    },
    {
      icon: FaClinicMedical,
      title: "Clinic & Outpatient Marketing",
      description:
        "Localized campaigns for outpatient clinics and specialty centers to boost appointment bookings.",
      features: [
        "Local SEO & Maps",
        "Appointment Automation",
        "Patient Education Content",
        "Clinic Reputation",
      ],
      color: "from-blue-500 to-cyan-500",
      iconColor: "text-white",
    },
    {
      icon: MdLocalHospital,
      title: "Diagnostic & Lab Promotion",
      description:
        "Marketing for diagnostic centers and labs to increase test volumes and B2B partnerships.",
      features: [
        "Package Promotion",
        "Physician Referrals",
        "Trust & Accuracy Messaging",
        "Online Bookings",
      ],
      color: "from-purple-500 to-fuchsia-500",
      iconColor: "text-white",
    },
    {
      icon: FaUserMd,
      title: "Doctor / Specialist Branding",
      description:
        "Individual physician marketing to grow practices, build thought leadership, and gain patient trust.",
      features: [
        "Profile Optimization",
        "Content for Specialties",
        "Teleconsultation Promotion",
        "Patient Reviews & Testimonials",
      ],
      color: "from-orange-500 to-amber-500",
      iconColor: "text-white",
    },
    {
      icon: FaAmbulance,
      title: "Emergency & Urgent Care Awareness",
      description:
        "Create awareness for emergency services and urgent-care networks for rapid triage and patient inflow.",
      features: [
        "Geo-targeted Ads",
        "Service Availability Updates",
        "Local Emergency SEO",
        "Rapid Contact CTAs",
      ],
      color: "from-red-500 to-rose-500",
      iconColor: "text-white",
    },
    {
      icon: FaStethoscope,
      title: "Telehealth & Virtual Care",
      description:
        "Position telemedicine offerings, increase adoption, and improve onboarding for virtual patients.",
      features: [
        "App & Platform Promotion",
        "Onboarding Funnels",
        "Care Pathway Content",
        "Conversion Optimization",
      ],
      color: "from-teal-500 to-cyan-500",
      iconColor: "text-white",
    },
  ];

  const segments = [
    {
      category: "Healthcare Facilities",
      items: [
        "Multi-specialty Hospitals",
        "Super-specialty Centers",
        "Outpatient Clinics",
        "Diagnostic Labs",
        "Rehabilitation Centers",
        "Ambulatory Surgery Centers",
        "Dialysis Centers",
        "Telehealth Providers",
      ],
      icon: FaHospital,
      iconColor: "text-white",
    },
    {
      category: "Medical Practices & Services",
      items: [
        "Cardiology",
        "Orthopedics",
        "Dermatology",
        "Pediatrics",
        "Oncology",
        "Neurology",
        "Dentistry",
        "Mental Health Services",
      ],
      icon: FaUserMd,
      iconColor: "text-white",
    },
    {
      category: "Support & Operations",
      items: [
        "Patient Engagement Platforms",
        "Appointment Systems",
        "Billing & Insurance Support",
        "Medical Device Vendors",
        "Medical Staffing & Training",
        "Healthcare IT & EMR Partners",
      ],
      icon: FaCogs,
      iconColor: "text-white",
    },
  ];

  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />
      <div className="min-h-screen">
        {/* HERO SECTION */}
        <div
          className="relative bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white py-24 overflow-hidden"
          style={{ backgroundImage: `url(${heroBg})` }}
        >
          <div className="absolute inset-0 bg-black opacity-40"></div>

          <div className="container mx-auto px-6 relative z-10 text-center">
            <h1 className="text-6xl font-bold text-white mb-6 pb-3">
              Healthcare Marketing
            </h1>

            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-10">
              Ethical, compliance-first marketing solutions for hospitals, clinics, and healthcare services.
            </p>
          </div>
        </div>

        {/* SERVICES SECTION */}
        <div className="pt-10 pb-20 bg-gradient-to-br from-gray-900 to-black text-white relative">
          <div className="container mx-auto px-6 relative">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-white mb-6">
                Healthcare Marketing Services
              </h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
                Specialized, compliant marketing crafted for hospitals, clinics and medical professionals.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <div
                  key={index}
                  className="group bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 hover:shadow-2xl transition-all duration-500 border border-gray-700 hover:border-blue-500/50 hover:-translate-y-2"
                >
                  <div className="flex items-center gap-4 mb-6">
                    <div className="bg-gradient-to-r from-cyan-500 to-blue-500 p-3 rounded-2xl shadow-lg flex items-center justify-center">
                      <service.icon className={`text-2xl ${service.iconColor}`} />
                    </div>
                    <h3 className="text-2xl font-bold">{service.title}</h3>
                  </div>

                  <p className="text-gray-300 leading-relaxed text-lg mb-6">
                    {service.description}
                  </p>

                  <div className="space-y-3">
                    {service.features.map((feature, i) => (
                      <div key={i} className="flex items-center">
                        <div className="bg-cyan-300/20 p-1 rounded-lg mr-3">
                          <FaCheck className="text-blue-400 text-sm" />
                        </div>
                        <span className="text-gray-300">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CHALLENGES GRID SECTION */}
        <div className="pt-10 pb-20 bg-white">
          <div className="container mx-auto px-6">
            <h2 className="text-5xl font-bold text-gray-900 text-center mb-16">
              Challenges Faced by the Healthcare Industry
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* LEFT — 4 CARDS */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* CARD 01 */}
                <div className="bg-white shadow-xl border border-gray-200 rounded-2xl p-6 hover:shadow-2xl transition">
                  <div className="text-4xl font-bold bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent mb-4">
                    01
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">
                    Strict Compliance & Regulations
                  </h3>
                  <p className="text-gray-600">
                    Healthcare advertising must follow strict legal and ethical rules — campaigns must be compliant and accurate.
                  </p>
                </div>

                {/* CARD 02 */}
                <div className="bg-white shadow-xl border border-gray-200 rounded-2xl p-6 hover:shadow-2xl transition">
                  <div className="text-4xl font-bold bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent mb-4">
                    02
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">
                    Building Patient Trust
                  </h3>
                  <p className="text-gray-600">
                    Patients prioritize trust and evidence; marketing must focus on transparency, outcomes and safety.
                  </p>
                </div>

                {/* CARD 03 */}
                <div className="bg-white shadow-xl border border-gray-200 rounded-2xl p-6 hover:shadow-2xl transition">
                  <div className="text-4xl font-bold bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent mb-4">
                    03
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">
                    Complex Patient Journeys
                  </h3>
                  <p className="text-gray-600">
                    From discovery to treatment follow-up, managing multiple touchpoints and clinician handoffs is challenging.
                  </p>
                </div>

                {/* CARD 04 */}
                <div className="bg-white shadow-xl border border-gray-200 rounded-2xl p-6 hover:shadow-2xl transition">
                  <div className="text-4xl font-bold bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent mb-4">
                    04
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">
                    Reputation & Review Management
                  </h3>
                  <p className="text-gray-600">
                    Online reviews and patient feedback significantly affect patient choice — reputation needs constant care.
                  </p>
                </div>
              </div>

              {/* RIGHT SIDE IMAGE */}
              <div className="flex justify-center">
                <img
                  src={ChallengesImage}
                  alt="Challenges"
                  className="rounded-3xl shadow-2xl w-full max-w-xl object-cover"
                />
              </div>
            </div>
          </div>
        </div>

        {/* HOW AMPLINOVA TURNS CHALLENGES INTO OPPORTUNITIES */}
        <div className="pt-10 pb-20 bg-white">
          <div className="container mx-auto px-6">
            <h2 className="text-5xl font-bold text-gray-900 text-center mb-16">
              How{" "}
              <span className="bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent underline decoration-blue-400 decoration-4">
                Amplinova
              </span>{" "}
              turns those challenges into opportunity
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl flex items-center justify-center shadow-xl">
                  <FaCheck className="text-white text-3xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Compliance-First Campaigns</h3>
                <p className="text-gray-600 leading-relaxed">
                  We build campaigns that meet medical advertising standards while communicating outcomes clearly.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl flex items-center justify-center shadow-xl">
                  <FaCheck className="text-white text-3xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Patient Trust & Education</h3>
                <p className="text-gray-600 leading-relaxed">
                  We use evidence-based content, outcomes messaging and patient stories to build credibility.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl flex items-center justify-center shadow-xl">
                  <FaCheck className="text-white text-3xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Optimized Patient Journeys</h3>
                <p className="text-gray-600 leading-relaxed">
                  From awareness to appointment, we design seamless funnels and clinician-friendly handoffs.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl flex items-center justify-center shadow-xl">
                  <FaCheck className="text-white text-3xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Reputation & Feedback Systems</h3>
                <p className="text-gray-600 leading-relaxed">
                  We implement proactive feedback loops, review management, and patient satisfaction tracking.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* SEGMENTS SECTION */}
        <div className="pt-10 pb-20 bg-white relative">
          <div className="container mx-auto px-6 relative">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-gray-900 mb-6">Healthcare Segments We Serve</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Deep expertise across hospitals, clinics, specialists, and healthcare operations
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {segments.map((segment, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-8 shadow-lg border border-cyan-100 hover:shadow-xl transition-all duration-300 group hover:border-cyan-300"
                >
                  <div className="bg-gradient-to-r from-cyan-500 to-blue-500 p-4 rounded-2xl inline-flex mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <segment.icon className={`text-2xl ${segment.iconColor}`} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">{segment.category}</h3>
                  <div className="space-y-3">
                    {segment.items.map((item, itemIndex) => (
                      <div
                        key={itemIndex}
                        className="flex items-center text-gray-700 group/item hover:text-blue-600 transition-colors duration-200"
                      >
                        <div className="bg-green-100 p-1 rounded-lg mr-3 group-hover/item:bg-green-200 transition-colors duration-200">
                          <FaCheck className="text-green-600 text-sm" />
                        </div>
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA SECTION */}
        <div className="relative pt-10 pb-20 bg-gradient-to-br from-gray-900 via-black to-gray-900 overflow-hidden">
          <div className="absolute inset-0 bg-black opacity-40"></div>

          <div className="container mx-auto px-6 text-center relative z-10">
            <h2 className="text-5xl font-bold text-white mb-8">
              Ready to Transform Your Healthcare Practice?
            </h2>
            <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto">
              Increase patient trust, improve appointment conversions, and scale ethically with our healthcare marketing expertise.
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-6">
              <button
                onClick={() => (window.location.href = "/contact-us")}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-12 py-5 rounded-xl font-semibold hover:scale-105 transition flex items-center gap-3"
              >
                Get a Free Healthcare Audit
                <FaArrowRight />
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};
export default HealthcareIndustry;
